import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Home from './components/Home';
import NotFound from './components/NotFound';
import Contact from './components/Contact';
import Partners from './components/Partners';
import { PrivacyPolicy, RefundPolicy, SecurityAndData, TermsOfService } from './components/LegalPages';
import ShopPage from './components/ShopPage';
import ShopLanding from './components/ShopLanding';
import Store from './components/Store';
import SharedPage from './components/SharedPage';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import Dashboard from './components/Dashboard';
import Layout from './components/Layout';
import AnnouncementStrip from './components/AnnouncementStrip';
import Production from './components/Production';
import Sales from './components/Sales';
import Users from './components/Users';
import Settings from './components/Settings';
import Help from './components/Help';
import { isOrgAdminRole } from './utils/roleUtils';
import { landingPath } from './utils/permissionUtils';
import SystemAdmin from './components/SystemAdmin';
import OrgAdmin from './components/OrgAdmin';
import InventoryManagement from './components/InventoryManagement';
import Inventory from './components/Inventory';
import Accounts from './components/Accounts';
import Quality from './components/Quality';
import AccountManagement from './components/AccountManagement';
import Affiliate from './components/Affiliate';
import { captureReferral } from './utils/referral';

/** allowRestricted: let an organization whose plan has ended in too (support is how they get unstuck). */
function ProtectedRoute({ children, allowRestricted = false }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" />;
  if (user.restricted && !allowRestricted) return <Navigate to="/org-admin" />;
  return children;
}

/**
 * Account Management is a partner login, not part of the organization being worked in, so it opens even when that organization's
 * plan has ended (the menu already shows it then). Anyone who is not an account manager goes where they would have gone.
 */
function AccountManagerRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" />;
  if (!user.isAccountManager) return <Navigate to={user.restricted ? '/org-admin' : '/dashboard'} />;
  return children;
}

/** The Affiliate screen is for affiliates only (a fact about the person, like an account manager: see AffiliateController). */
function AffiliateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  if (!user.isAffiliate) return <Navigate to={user.restricted ? '/org-admin' : '/dashboard'} />;
  return children;
}

// System Admin sections that now live in the Tech Support module: their old addresses go there
const MOVED_TO_TECH_SUPPORT = ['feedback', 'assistant-questions', 'material-master', 'services-master'];

function SystemAdminRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  const isSystemAdmin = user.isSystemAdmin ||
    (user.roles && user.roles.some(role => role === 'ROLE_SYSTEM_ADMIN' || role === 'SYSTEM_ADMIN'));
  
  if (!isSystemAdmin) {
    return <Navigate to={user.isTechSupport ? '/tech-support' : '/dashboard'} />;
  }
  
  return children;
}

/** Tech Support is iBigDo staff who answer customers; System Admin reaches it too. */
function TechSupportRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" />;
  const allowed = user.isSystemAdmin || user.isTechSupport ||
    (user.roles && user.roles.some(role => role === 'ROLE_SYSTEM_ADMIN' || role === 'SYSTEM_ADMIN'));
  if (!allowed) return <Navigate to="/dashboard" />;
  return children;
}

function SystemAdminPage() {
  const { section } = useParams();
  if (MOVED_TO_TECH_SUPPORT.includes(section)) return <Navigate to={`/tech-support/${section}`} replace />;
  return <SystemAdmin module="system-admin" />;
}

function OrgAdminRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  const isSystemAdmin = user.isSystemAdmin || 
    (user.roles && user.roles.some(role => role === 'ROLE_SYSTEM_ADMIN' || role === 'SYSTEM_ADMIN'));
  const isOrgAdmin = user.roles && user.roles.some(isOrgAdminRole);
  
  if (!isSystemAdmin && !isOrgAdmin) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <>
    <AnnouncementStrip />
    <Routes>
      <Route path="/login" element={!user ? <Login /> : <Navigate to={landingPath(user)} />} />
      <Route path="/register" element={!user ? <Register /> : <Navigate to={landingPath(user)} />} />
      <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to={landingPath(user)} />} />
      <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to={landingPath(user)} />} />
      <Route path="/verify-email" element={!user ? <VerifyEmail /> : <Navigate to={landingPath(user)} />} />
      <Route path="/" element={!user ? <Home /> : <Navigate to={landingPath(user)} />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/partners" element={<Partners />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/security" element={<SecurityAndData />} />
      {/* A document shared on WhatsApp: opens for anyone with the link, signed in or not */}
      <Route path="/share/:token" element={<SharedPage />} />
      {/* A store's public pages: open to anyone, signed in or not */}
      <Route path="/shop" element={<ShopLanding />} />
      <Route path="/shop/:slug/:page?/:id?" element={<ShopPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/production/:section?" element={
        <ProtectedRoute>
          <Layout>
            <Production />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/store/:section?" element={
        <ProtectedRoute>
          <Layout>
            <Store />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/sales/:section?" element={
        <ProtectedRoute>
          <Layout>
            <Sales />
          </Layout>
        </ProtectedRoute>
      } />
      {/* Keep old routes for backward compatibility - redirect to sales */}
      <Route path="/customers" element={<Navigate to="/sales" replace />} />
      <Route path="/orders" element={<Navigate to="/sales" replace />} />
      <Route path="/users" element={
        <ProtectedRoute>
          <Layout>
            <Users />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/help/:section?" element={
        <ProtectedRoute allowRestricted>
          <Layout>
            <Help />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout>
            <Settings />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/org-admin/:section?" element={
        <OrgAdminRoute>
          <Layout>
            <OrgAdmin />
          </Layout>
        </OrgAdminRoute>
      } />
      <Route path="/system-admin/:section?" element={
        <SystemAdminRoute>
          <Layout>
            <SystemAdminPage />
          </Layout>
        </SystemAdminRoute>
      } />
      <Route path="/tech-support/:section?" element={
        <TechSupportRoute>
          <Layout>
            <SystemAdmin module="tech-support" />
          </Layout>
        </TechSupportRoute>
      } />
      <Route path="/purchase/:section?" element={
        <ProtectedRoute>
          <Layout>
            <InventoryManagement />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/inventory/:section?" element={
        <ProtectedRoute>
          <Layout>
            <Inventory />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/quality" element={
        <ProtectedRoute>
          <Layout>
            <Quality />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/accounts/:section?" element={
        <ProtectedRoute>
          <Layout>
            <Accounts />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/account-management/:section?" element={
        <AccountManagerRoute>
          <Layout>
            <AccountManagement />
          </Layout>
        </AccountManagerRoute>
      } />
      <Route path="/affiliate" element={
        <AffiliateRoute>
          <Layout>
            <Affiliate />
          </Layout>
        </AffiliateRoute>
      } />
      {/* Anything else: a mistyped address, an old bookmark. The server answers it with status 404 and this page too */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </>
  );
}

// A link with ?ref=CODE (ibigdo.com/register?ref=CODE, or any page) is remembered for 30 days, so the Referred by box is filled in
// even if the person comes back to sign up later
captureReferral();

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;